/**
 * Finnish gross-to-net salary estimate for wage earners.
 *
 * Minimal use:   calculateNetSalary({ grossMonthly: 4000 })
 * Accurate use:  calculateNetSalary({ grossMonthly: 4000, municipalTaxRatePct: 5.36,
 *                                     churchTaxRatePct: 1.0, age: 34, dependentChildren: 2 })
 *
 * Parameter sources (2026):
 * - State tax scale: annual scale act (Finlex) / Veronmaksajat
 * - Contribution rates: Verohallinto withholding-basis decision for 2026
 * - Credits and deductions: Tuloverolaki as amended for 2026 (vero.fi "Muutokset
 *   veroperusteisiin 2026")
 * - Municipal rates per municipality: Vero annual decision / Kuntaliitto Excel
 *
 * Model notes:
 * - Assumes all income is wage income, standard deductions only, full-year residence.
 * - 2026 removed the standard work-room deduction and union fee deductibility,
 *   which makes this standard model MORE representative than in earlier years.
 * - Ignores holiday bonus timing, fringe benefits, and withholding rounding; this
 *   reproduces final taxation, not the tax-card percentage.
 * - No per-municipality tax-rate table is wired in yet: callers that don't pass
 *   municipalTaxRatePct get the national average and isGenericEstimate: true.
 */

// ---------------------------------------------------------------------------
// Yearly parameters. Update this block each December (~15 numbers).
// ---------------------------------------------------------------------------

export interface TaxYearParams {
  year: number;
  /** Progressive state tax brackets: [lowerBound, taxAtLowerBound, marginalRate] */
  stateBrackets: Array<[number, number, number]>;
  /** Employee pension contribution (TyEL), share of gross. Flat for all ages since 2026. */
  pensionRate: number;
  pensionMinAge: number;
  pensionMaxAge: number; // upper age varies 68-70 by birth year; 68 used as approximation
  /** Employee unemployment insurance contribution, share of gross */
  unemploymentRate: number;
  unemploymentMinAge: number;
  unemploymentMaxAge: number;
  /** Health insurance daily allowance contribution (päivärahamaksu), share of gross */
  dailyAllowanceRate: number;
  dailyAllowanceIncomeThreshold: number; // levied on full wage once annual wage >= threshold
  /** Health insurance medical care contribution (sairaanhoitomaksu), on taxable income */
  medicalCareRate: number;
  /** Standard work-expense deduction (tulonhankkimisvähennys) */
  workExpenseDeduction: number;
  /** Basic allowance (perusvähennys) */
  basicAllowanceMax: number;
  basicAllowancePhaseOutRate: number;
  /** Earned income tax credit (työtulovähennys) */
  witcRate: number;               // accrual: share of gross wage income
  witcMax: number;
  witcAge65Bonus: number;         // if 65 before start of tax year
  witcChildBonus: number;         // per minor dependent child
  witcPhaseOutRate: number;       // reduction rate on pure earned income above threshold
  witcPhaseOutStart: number;
  witcPhaseOutEnd: number;        // reduction stops here (credit no longer falls to zero)
  /** Public broadcasting tax (Yle) */
  yleRate: number;
  yleThreshold: number;
  yleMax: number;
  /** Average municipal income tax rate, % (income-weighted). Used when none given. */
  avgMunicipalRatePct: number;
}

export const TAX_PARAMS_2026: TaxYearParams = {
  year: 2026,
  stateBrackets: [
    [0, 0, 0.1264],
    [22_000, 2_780.8, 0.19],
    [32_600, 4_794.8, 0.3025],
    [40_100, 7_063.55, 0.3325],
    [52_100, 11_053.55, 0.375],
  ],
  pensionRate: 0.073,
  pensionMinAge: 17,
  pensionMaxAge: 68,
  unemploymentRate: 0.0089,
  unemploymentMinAge: 18,
  unemploymentMaxAge: 64,
  dailyAllowanceRate: 0.0088,
  dailyAllowanceIncomeThreshold: 17_255,
  medicalCareRate: 0.011,
  workExpenseDeduction: 750,
  basicAllowanceMax: 4_265,
  basicAllowancePhaseOutRate: 0.18,
  witcRate: 0.18,
  witcMax: 3_430,
  witcAge65Bonus: 1_200,
  witcChildBonus: 105,
  witcPhaseOutRate: 0.02,
  witcPhaseOutStart: 35_000,
  witcPhaseOutEnd: 50_550,
  yleRate: 0.025,
  yleThreshold: 15_150,
  yleMax: 160,
  avgMunicipalRatePct: 7.57,
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface NetSalaryInput {
  /** Provide one of these. If both given, grossAnnual wins. */
  grossMonthly?: number;
  grossAnnual?: number;
  /** Municipal income tax %, e.g. Helsinki 2026. Defaults to the national average. */
  municipalTaxRatePct?: number;
  /** Church tax %, 1.0-2.25. Default 0 (not a member / unknown). */
  churchTaxRatePct?: number;
  /** Age in years. Affects which contributions apply and the 65+ credit bonus. */
  age?: number;
  /** Minor children the person is a guardian of. Raises the earned income credit. */
  dependentChildren?: number;
  /** Sole guardian doubles the per-child credit bonus. */
  isSoleGuardian?: boolean;
  /** Include the Yle public broadcasting tax. Default true. */
  includeYleTax?: boolean;
  /** Parameter set; defaults to 2026. */
  params?: TaxYearParams;
}

export interface NetSalaryResult {
  grossAnnual: number;
  contributions: { pension: number; unemployment: number; dailyAllowance: number; total: number };
  taxableIncome: number;
  taxes: { state: number; municipal: number; medicalCare: number; church: number; yle: number; total: number };
  earnedIncomeCredit: number;
  totalDeductions: number;      // contributions + taxes
  netAnnual: number;
  netMonthly: number;
  effectiveRatePct: number;     // totalDeductions / gross
  /** True when defaults were used for municipality etc: treat as a generic estimate. */
  isGenericEstimate: boolean;
}

export function calculateNetSalary(input: NetSalaryInput): NetSalaryResult {
  const p = input.params ?? TAX_PARAMS_2026;
  const gross = input.grossAnnual ?? (input.grossMonthly ?? 0) * 12;
  if (!(gross > 0)) throw new Error("Provide a positive grossMonthly or grossAnnual");

  const age = input.age; // undefined = assume standard working-age adult
  const inAgeRange = (min: number, max: number) => age === undefined || (age >= min && age <= max);

  // 1. Employee social insurance contributions (withheld from gross, tax deductible)
  const pension = inAgeRange(p.pensionMinAge, p.pensionMaxAge) ? gross * p.pensionRate : 0;
  const unemployment = inAgeRange(p.unemploymentMinAge, p.unemploymentMaxAge) ? gross * p.unemploymentRate : 0;
  const dailyAllowance = gross >= p.dailyAllowanceIncomeThreshold ? gross * p.dailyAllowanceRate : 0;
  const contributionsTotal = pension + unemployment + dailyAllowance;

  // 2. Pure earned income (puhdas ansiotulo): gross minus work expenses.
  //    NOTE: contributions are deducted AFTER this stage; the earned income credit
  //    phase-out and the Yle tax use this pre-contribution figure.
  const pureEarnedIncome = Math.max(0, gross - Math.min(gross, p.workExpenseDeduction));

  // 3. Income after contribution deduction, then basic allowance -> taxable income
  const incomeAfterContributions = Math.max(0, pureEarnedIncome - contributionsTotal);
  const basicAllowance = Math.max(
    0,
    Math.min(
      incomeAfterContributions,
      p.basicAllowanceMax -
        p.basicAllowancePhaseOutRate * Math.max(0, incomeAfterContributions - p.basicAllowanceMax),
    ),
  );
  const taxableIncome = incomeAfterContributions - basicAllowance;

  // 4. Taxes on taxable income
  const stateTaxGross = stateTax(taxableIncome, p.stateBrackets);
  const municipalRatePct = input.municipalTaxRatePct ?? p.avgMunicipalRatePct;
  const municipalGross = taxableIncome * (municipalRatePct / 100);
  const medicalCareGross = taxableIncome * p.medicalCareRate;
  const churchGross = taxableIncome * ((input.churchTaxRatePct ?? 0) / 100);

  // 5. Earned income tax credit (työtulovähennys)
  let creditMax = p.witcMax;
  if (age !== undefined && age >= 66) creditMax += p.witcAge65Bonus; // turned 65 before the tax year
  const childMultiplier = input.isSoleGuardian ? 2 : 1;
  creditMax += (input.dependentChildren ?? 0) * p.witcChildBonus * childMultiplier;
  const phaseOutBase = Math.min(
    Math.max(0, pureEarnedIncome - p.witcPhaseOutStart),
    p.witcPhaseOutEnd - p.witcPhaseOutStart,
  );
  const credit = Math.max(
    0,
    Math.min(creditMax, gross * p.witcRate) - p.witcPhaseOutRate * phaseOutBase,
  );

  // 6. Apply credit: first against state tax, overflow proportionally against
  //    municipal tax, medical care contribution and church tax (never Yle tax).
  const state = Math.max(0, stateTaxGross - credit);
  const overflow = Math.max(0, credit - stateTaxGross);
  const overflowPool = municipalGross + medicalCareGross + churchGross;
  const overflowFactor = overflowPool > 0 ? Math.max(0, 1 - overflow / overflowPool) : 0;
  const municipal = municipalGross * overflowFactor;
  const medicalCare = medicalCareGross * overflowFactor;
  const church = churchGross * overflowFactor;

  // 7. Yle tax: on pure earned income, capped; credits do not reduce it
  const yle =
    input.includeYleTax === false || (age !== undefined && age < 18)
      ? 0
      : Math.min(p.yleMax, Math.max(0, pureEarnedIncome - p.yleThreshold) * p.yleRate);

  const taxesTotal = state + municipal + medicalCare + church + yle;
  const totalDeductions = contributionsTotal + taxesTotal;
  const netAnnual = gross - totalDeductions;

  const r = (x: number) => Math.round(x * 100) / 100;
  return {
    grossAnnual: r(gross),
    contributions: {
      pension: r(pension),
      unemployment: r(unemployment),
      dailyAllowance: r(dailyAllowance),
      total: r(contributionsTotal),
    },
    taxableIncome: r(taxableIncome),
    taxes: {
      state: r(state),
      municipal: r(municipal),
      medicalCare: r(medicalCare),
      church: r(church),
      yle: r(yle),
      total: r(taxesTotal),
    },
    earnedIncomeCredit: r(credit),
    totalDeductions: r(totalDeductions),
    netAnnual: r(netAnnual),
    netMonthly: r(netAnnual / 12),
    effectiveRatePct: r((totalDeductions / gross) * 100),
    isGenericEstimate: input.municipalTaxRatePct === undefined,
  };
}

function stateTax(taxable: number, brackets: Array<[number, number, number]>): number {
  let tax = 0;
  for (const [lower, base, rate] of brackets) {
    if (taxable > lower) tax = base + (taxable - lower) * rate;
  }
  return tax;
}
